import { HttpErrorResponse }                                            from '@angular/common/http';
import { Component, OnInit, ViewChild }                                 from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MatSelectionList }                                             from '@angular/material/list';

import { ToastrService }                                                                                from 'ngx-toastr';
import { BehaviorSubject, catchError, distinctUntilChanged, Observable, startWith, Subject, takeUntil } from 'rxjs';


import { defaultCPFMask }       from '@shared/utils/masks/cpf-mask';
import { emailValidatorRegex }  from '@shared/utils/validators/email-validator-regex';
import { ReactiveRequired }     from '@shared/form-utilities/reactive-required.directive';
import { ProofreaderApplicant } from '@models/proofreader/proofreader-applicant';
import { Mask }                 from '@shared/models/utils/mask';
import { CpfValidator }         from '@shared/utils/validators/cpf-cnpj-validator';

import { ErrorService }           from '@shared/utils/error/error.service';
import { ApiProofreadersService } from '@services/api-proofreaders.service';
import { ConfirmationModalComponent } from '@modals/confirmation-modal/confirmation-modal.component';


@Component({
  selector:    'red-proofreader-applicant',
  templateUrl: './proofreader-applicant.component.html',
  styleUrls:   [ './proofreader-applicant.component.scss' ]
})
export class ProofreaderApplicantComponent implements OnInit {

  private readonly unsubscribe$: Subject<void>;

  private isLoadingSource$: BehaviorSubject<boolean>;

  public isLoading$: Observable<boolean>;

  public formGroup:      FormGroup;
  public selectionGroup: FormGroup;
  public skillOptions:   string[];
  public cpfMask:        Mask;

  public all:            FormControl;
  public allSelected:    boolean;
  public someSelected:   boolean;
  public amountSelected: number;

  @ViewChild('skillsSelectionList') public skillsSelectionList: MatSelectionList;

  constructor(
    private formBuilder:        FormBuilder,
    private toastr:             ToastrService,
    private errorService:       ErrorService,
    private proofreaderService: ApiProofreadersService,
  ) {
    this.cpfMask = defaultCPFMask;

    this.skillOptions = [
      'Enem',    'Fundamental Anos Finais', 'Fuvest',
      'Unicamp', 'Outros vestibulares',     'Sem experiência'
    ];
  }

  ngOnInit(): void {
    this.initIsLoading();
    this.initForm();
  }

  private initIsLoading() {
    this.isLoadingSource$ = new BehaviorSubject<boolean>(false);
    this.isLoading$       = this.isLoadingSource$.asObservable().pipe(
      startWith(false as boolean),
      distinctUntilChanged(),
      takeUntil(this.unsubscribe$),
    );
  }

  private updateIsLoading(status: boolean) {
    this.isLoadingSource$.next(status);
  }

  public get experiencesCtrl(): FormControl { return this.formGroup.get('dscExperiencia') as FormControl; }
  
  public get descProofreaderCandidateCtrl(): FormControl { return this.formGroup.get('dscProfessorCandidatoCurso') as FormControl; }

  private initForm() {
    const emailValidators: ValidatorFn[] = [ ReactiveRequired, Validators.pattern(emailValidatorRegex) ];

    this.formGroup = this.formBuilder.group({
      nome:                          this.formBuilder.control(null,  [ Validators.required ]),
      cpf:                           this.formBuilder.control(null,  [ Validators.required, CpfValidator ]),
      email:                         this.formBuilder.control(null,  [ ...emailValidators, Validators.required ]),
      linkedin:                      this.formBuilder.control(null),
      indNivelSuperior:              this.formBuilder.control(false, [ Validators.required ]),
      instituicaoEnsino:             this.formBuilder.control(null,  [ Validators.required ]),
      professorCandidatoCurso:       this.formBuilder.control(null,  [ Validators.required ]),
      dscProfessorCandidatoCurso:    this.formBuilder.control(null),
      indExperienciaCorrecaoTexto:   this.formBuilder.control(null,  [ Validators.required ]),
      dscExperiencia:                this.formBuilder.control(null,  [ Validators.required ]),
      expVestibulares:               this.formBuilder.control([]),
      professorCandidatoGrauEscolar: this.formBuilder.control(null,  [ Validators.required ])
    });
  }

  public submit() {
    if (this.formGroup.invalid) return;

    const handleError = (error: HttpErrorResponse) => {
      this.updateIsLoading(false);
      return this.errorService.handleFormError(error);
    };

    const handleSuccess = (applicant: ProofreaderApplicant) => {
      if (!applicant) return;
      this.updateIsLoading(false);
      this.toastr.success('Formulário enviado com sucesso !');
    };

    this.proofreaderService
        .registerApplicant(this.formGroup.getRawValue())
        .pipe(catchError(handleError))
        .subscribe(handleSuccess);
  }

}
